import React, { useState } from 'react';
import { formatSimpleCurrency } from '../utils/currencyUtils';
import { printSaleInvoice, createSaleInvoiceData } from '../utils/invoiceUtils';

interface SellCarModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (customerName: string, customerPhone: string, customerLicense: string, sellingPrice: number) => void;
  carInfo: {
    brand: string;
    model: string;
    plate?: string;
    currentPrice?: number;
  };
}

export const SellCarModal: React.FC<SellCarModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carInfo
}) => {
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerLicense, setCustomerLicense] = useState('');
  const [sellingPrice, setSellingPrice] = useState(carInfo.currentPrice?.toString() || '');
  const [errors, setErrors] = useState<{customerName?: string; customerPhone?: string; customerLicense?: string; sellingPrice?: string}>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    const newErrors: {customerName?: string; customerPhone?: string; customerLicense?: string; sellingPrice?: string} = {};
    
    if (!customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    }
    
    if (!customerPhone.trim()) {
      newErrors.customerPhone = 'Customer phone number is required';
    }
    
    if (!customerLicense.trim()) {
      newErrors.customerLicense = 'Driver\'s license number is required';
    }
    
    const price = parseFloat(sellingPrice);
    if (!sellingPrice.trim() || isNaN(price) || price <= 0) {
      newErrors.sellingPrice = 'Valid selling price is required';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onConfirm(customerName.trim(), customerPhone.trim(), customerLicense.trim(), price);
    handleClose();
  };

  const handleClose = () => {
    setCustomerName('');
    setCustomerPhone('');
    setCustomerLicense('');
    setSellingPrice(carInfo.currentPrice?.toString() || '');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 text-white rounded-t-2xl">
          <h2 className="text-xl font-semibold">💰 Sell Car</h2>
          <p className="text-green-100 text-sm mt-1">
            {carInfo.brand} {carInfo.model} {carInfo.plate && `(${carInfo.plate})`}
          </p>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Customer Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => {
                setCustomerName(e.target.value);
                if (errors.customerName) {
                  setErrors(prev => ({ ...prev, customerName: undefined }));
                }
              }}
              placeholder="Enter customer's full name"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.customerName ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.customerName && (
              <p className="text-red-500 text-sm mt-1">{errors.customerName}</p>
            )}
          </div>

          {/* Customer Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={customerPhone}
              onChange={(e) => {
                setCustomerPhone(e.target.value);
                if (errors.customerPhone) {
                  setErrors(prev => ({ ...prev, customerPhone: undefined }));
                }
              }}
              placeholder="Enter customer's phone number"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.customerPhone ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.customerPhone && (
              <p className="text-red-500 text-sm mt-1">{errors.customerPhone}</p>
            )}
          </div>

          {/* Driver's License */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Driver's License Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={customerLicense}
              onChange={(e) => {
                setCustomerLicense(e.target.value);
                if (errors.customerLicense) {
                  setErrors(prev => ({ ...prev, customerLicense: undefined }));
                }
              }}
              placeholder="Enter driver's license number"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.customerLicense ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.customerLicense && (
              <p className="text-red-500 text-sm mt-1">{errors.customerLicense}</p>
            )}
          </div>

          {/* Selling Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selling Price (DA) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={sellingPrice}
              onChange={(e) => {
                setSellingPrice(e.target.value);
                if (errors.sellingPrice) {
                  setErrors(prev => ({ ...prev, sellingPrice: undefined }));
                }
              }}
              placeholder="Enter final selling price"
              min="0"
              step="1000"
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.sellingPrice ? 'border-red-300' : 'border-gray-200'
              }`}
            />
            {errors.sellingPrice && (
              <p className="text-red-500 text-sm mt-1">{errors.sellingPrice}</p>
            )}
            {sellingPrice && !isNaN(parseFloat(sellingPrice)) && (
              <p className="text-green-600 text-sm mt-1">
                Final price: {formatSimpleCurrency(parseFloat(sellingPrice))}
              </p>
            )}
          </div>

          {/* Price Comparison */}
          {carInfo.currentPrice && sellingPrice && !isNaN(parseFloat(sellingPrice)) && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Price Comparison</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Listed Price:</span>
                  <span>{formatSimpleCurrency(carInfo.currentPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Selling Price:</span>
                  <span className="font-medium">{formatSimpleCurrency(parseFloat(sellingPrice))}</span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <span className="text-gray-600">Difference:</span>
                  <span className={`font-medium ${
                    parseFloat(sellingPrice) >= carInfo.currentPrice ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {parseFloat(sellingPrice) >= carInfo.currentPrice ? '+' : ''}
                    {formatSimpleCurrency(parseFloat(sellingPrice) - carInfo.currentPrice)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-between items-center pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => {
                  if (customerName && customerPhone && customerLicense && sellingPrice) {
                    const invoiceData = createSaleInvoiceData(
                      {
                        customerName: customerName.trim(),
                        customerPhone: customerPhone.trim(),
                        customerLicense: customerLicense.trim(),
                        finalSalePrice: parseFloat(sellingPrice),
                        originalPrice: carInfo.currentPrice,
                      },
                      carInfo
                    );
                    printSaleInvoice(invoiceData);
                  } else {
                    alert('Please fill in all required fields before printing invoice.');
                  }
                }}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Print Invoice</span>
              </button>
              
              <button
                type="submit"
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Complete Sale
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
